import {
  FluidAlignment,
  FluidIconConfig,
  FluidIconKey,
} from '@lmig/fluid-core';
import { FunctionalComponent, h } from '@stencil/core';

import { FluidFunctionalComponentProps } from '../../../model/functional/fluid-functional-component-props.interface';
import { FluidI18nStore } from '../../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { themeVar } from '../../../utils/themes/theme.variables';
import { FluidCard } from '../fluid-card';
import {
  getActionButtonConfig,
  getBadgeConfig,
} from '../utils/fluid-card.utils';

export interface FluidCardTranslationOptions {
  cardTitle?: string;
  titleLabel?: string;
  subtitle?: string;
}

export interface CardProps extends FluidFunctionalComponentProps {
  host: FluidCard;
  i18nStore?: FluidI18nStore;
  fluidTranslate?: FluidCardTranslationOptions;
}

// ================================================================ //
// -- Public Functional Components

/**
 * Renders entire Card header, including optional "collapse", "refresh",
 * & "action" buttons, as well as optional "header" slot. NOTE: "header"
 * slot, "action button", & "collapse toggle" are conditionally rendered.
 */
export const CardHeader: FunctionalComponent<CardProps> = (
  props: CardProps
) => {
  const { host, i18nStore, fluidTranslate } = props;
  const noWrap =
    !host._hasHeaderSlot && !host.refreshable && !getActionButtonConfig(host);

  return (
    (!!host._title || !!host._hasHeaderSlot) && (
      <div
        class={{
          'card-header': true,
          'card-collapsed': host.collapsed,
          'header-border': !host.removeHeaderBorder && !host.collapsed,
          'no-wrap': noWrap,
        }}>
        {hasHeaderLeft(host) && (
          <div class='header-left'>
            <CardCollapseToggle host={host} position={FluidAlignment.LEFT} />
            <CardTitle
              host={host}
              i18nStore={i18nStore}
              fluidTranslate={fluidTranslate}
            />
          </div>
        )}
        <CardHeaderRefresh host={host} />
        {!!host._hasHeaderSlot && (
          <div class='header-center'>
            <slot name='header' />
          </div>
        )}
        {hasHeaderRight(host) && (
          <div class='header-right'>
            <CardHeaderAction host={host} />
            <CardCollapseToggle host={host} position={FluidAlignment.RIGHT} />
          </div>
        )}
      </div>
    )
  );
};

// ================================================================ //
// -- Private Functional Components

/**
 * Creates Card header's title section, including optional "title
 * label", Badge, & subtitle.
 */
const CardTitle: FunctionalComponent<CardProps> = (props: CardProps) => {
  const { host, i18nStore, fluidTranslate } = props;
  const { layoutOnly } = host;
  const bodyId = host['_bodyId'];
  const badgeConfig = getBadgeConfig(host);
  const TitleTag = layoutOnly ? 'h2' : 'span'; // Maintain existing semantics when card is not in layout only mode.
  return (
    <div
      role={host.collapsible ? 'button' : null}
      aria-expanded={host.collapsible ? String(!host.collapsed) : null}
      aria-controls={host.collapsible ? bodyId : null}
      onClick={() => host.toggleCollapsed()}
      class={{
        'card-title': true,
        collapsible: host.collapsible,
      }}>
      <div class='title-container'>
        {(!!host.titleLabel ||
          !!i18nStore?.translate(fluidTranslate?.titleLabel)) && (
          <span class='title-label'>
            {`${
              i18nStore?.translate(fluidTranslate?.titleLabel) ||
              host.titleLabel
            }: `}
          </span>
        )}
        <TitleTag class='title-text'>
          {i18nStore?.translate(fluidTranslate?.cardTitle) || host._title}
        </TitleTag>
        {!!badgeConfig && (
          <fluid-badge class='title-badge' config={badgeConfig} />
        )}
      </div>
      {(!!host.subtitle ||
        !!i18nStore?.translate(fluidTranslate?.subtitle)) && (
        <span class='card-subtitle'>
          {i18nStore?.translate(fluidTranslate?.subtitle) || host.subtitle}
        </span>
      )}
    </div>
  );
};

/**
 * Renders "collapse toggle" button for Card header.
 */
const CardCollapseToggle: FunctionalComponent<CardProps> = (
  props: CardProps & {
    position: Exclude<FluidAlignment, FluidAlignment.CENTER>;
  }
) => {
  const { host, position } = props;
  const bodyId = host['_bodyId'];

  return (
    hasCollapseToggle(host, position) && (
      <button
        key='collapse-toggle-button'
        title={host.collapsed ? 'Expand' : 'Collapse'}
        aria-label={host.collapsed ? 'Expand' : 'Collapse'}
        aria-expanded={String(!host.collapsed)}
        aria-controls={bodyId}
        onClick={() => host.toggleCollapsed()}
        class={{
          'collapse-toggle': true,
          [`position-${position}`]: true,
          collapsed: host.collapsed,
          expanded: !host.collapsed,
        }}>
        <fluid-icon
          key='collapse-toggle-icon'
          class='collapse-toggle-icon'
          config={{
            ...themeVar<FluidIconConfig>(host.theme, 'icons.card.collapse'),
            key: host.collapsed ? FluidIconKey.EXPAND : FluidIconKey.COLLAPSE,
            theme: host.theme,
            overrideGlobalTheme: true,
          }}
        />
      </button>
    )
  );
};

/**
 * Renders "action button" for Card header (if defined).
 */
const CardHeaderAction: FunctionalComponent<CardProps> = (props: CardProps) => {
  const { host } = props;
  const actionButtonConfig = getActionButtonConfig(host);

  return (
    !!actionButtonConfig && (
      <fluid-button
        key='action-button'
        class='header-action'
        onClick={() => host.action.emit()}
        config={actionButtonConfig}
      />
    )
  );
};

// ================================================================ //
// -- Deprecated Functional Components

/**
 * Renders Icon-Only "refresh" Button for Card header (with or without Popover).
 * @deprecated
 */
const CardHeaderRefresh: FunctionalComponent<CardProps> = (
  props: CardProps
) => {
  const { host } = props;

  return (
    !!host.refreshable && (
      <fluid-button
        class='refresh-toggle'
        onClick={() => host.refreshed.emit()}
        config={{
          theme: host.theme,
          overrideGlobalTheme: true,
          inheritIconColor: true,
          icon: {
            key: FluidIconKey.REFRESH,
            rotate: host.refreshing,
            theme: host.theme,
            overrideGlobalTheme: true,
          },
          popoverConfig: {
            message: host.refreshTooltip, // Popover won't render if undefined
            placement: host.refreshTooltipPlacement,
          },
        }}
      />
    )
  );
};

// ================================================================ //
// -- Functional Component Utilities (Private)

/**
 * Returns true if Card should render ".header-left" section within
 * its header - this improves component responsiveness by preventing
 * empty header sections from being rendered unnecessarily.
 */
const hasHeaderLeft = (host: FluidCard): boolean => {
  return (
    hasCollapseToggle(host, FluidAlignment.LEFT) ||
    !!host._title ||
    !!getBadgeConfig(host) ||
    !!getActionButtonConfig(host)
  );
};

/**
 * Returns true if Card should render ".header-right" section within
 * its header - this improves component responsiveness by preventing
 * empty header sections from being rendered unnecessarily.
 */
const hasHeaderRight = (host: FluidCard): boolean => {
  return (
    hasCollapseToggle(host, FluidAlignment.RIGHT) ||
    !!getActionButtonConfig(host)
  );
};

/**
 * Returns true if Card should render a "collapse toggle" button, false
 * if not. Since some themes render the "collapse toggle" button on the
 * right & some render it on the left, this utility function is called
 * twice during each render (once for each side/"position") - this
 * function will only return true if the "position" (i.e. side) matches
 * the "collapse toggle" position defined in the theme's global variables.
 * @param host - the FluidCard instance to check
 * @param position - side of Card (left/right) to render collapse toggle
 */
const hasCollapseToggle = (
  host: FluidCard,
  position: Exclude<FluidAlignment, FluidAlignment.CENTER>
): boolean => {
  return (
    !!host.collapsible &&
    position ===
      themeVar<FluidAlignment>(
        host.theme,
        'prop_defaults.card.collapse_toggle_position'
      )
  );
};
